import { useEffect, useState } from "react";
import { collection, query, where, orderBy, onSnapshot } from "firebase/firestore";
import { db } from "../firebase";
import { useAuth } from "../contexts/AuthContext";
import type { Trip } from "../types";

export interface TripWithId extends Trip {
  id: string;
}

/** The last list per user, so a page coming back draws its trips at once. */
const lastSeen = new Map<string, TripWithId[]>();

export function useTrips() {
  const { user } = useAuth();
  const [trips, setTrips] = useState<TripWithId[]>(() => (user ? lastSeen.get(user.uid) : undefined) ?? []);
  const [loading, setLoading] = useState(() => !(user && lastSeen.has(user.uid)));

  useEffect(() => {
    if (!user) {
      setTrips([]);
      setLoading(false);
      return;
    }

    const q = query(
      collection(db, "trips"),
      where("memberUserIds", "array-contains", user.uid),
      orderBy("startDate", "desc")
    );

    const unsub = onSnapshot(q, (snapshot) => {
      const data = snapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      })) as TripWithId[];
      lastSeen.set(user.uid, data);
      setTrips(data);
      setLoading(false);
    });

    return unsub;
  }, [user]);

  return { trips, loading };
}
